import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styles from './HomeSettings.module.scss';
import Loader from '../../Molecules/Loader/Loader';
import MenuTree, { ICONS } from '../../Molecules/NavigationMenu/MenuTree';
import MenuItemEditor from '../../Molecules/NavigationMenu/MenuItemEditor';
import ConfirmDialog from '../../Molecules/NavigationMenu/ConfirmDialog';
import {
  MENU_MODE,
  PLACEMENTS,
  buildPlacementTree,
  collectParentIds,
  countPlacements,
  filterPlacementTree,
  findPlacementNode,
  useDeletePlacement,
  useItemPlacements,
  useMenuMode,
  useMovePlacement,
  usePlacements,
  planPlacementMove,
  useReorderSiblings,
  useSetMenuMode,
  useUpdatePlacement,
} from '../../../Hooks/NavigationMenu';

/**
 * إعدادات القوائم — the display-mode switch plus one editor per placement.
 *
 * Two things this screen must never do, both learned the hard way elsewhere in
 * this CMS:
 *
 *   1. Let an operator edit in silence. While the mode is `كما هو حالياً`
 *      nothing here reaches the live site, and the status pill in the header
 *      says so on every tab — rather than leaving someone to wonder why their
 *      edit did nothing.
 *   2. Hide a missing backend behind a shim. If the settings endpoint is not
 *      reachable the control renders disabled with the route named, instead of
 *      pretending to save.
 *
 * REDESIGNED 2026-09-29. The five placements were pill buttons wrapping to two
 * rows; they are now a real underline TABLIST with live counts and arrow-key
 * navigation. The panel gained a sticky toolbar — filter, expand/collapse all,
 * add — because the main menu holds 58 rows and had neither search nor collapse.
 */

/**
 * Above this many rows the tree opens COLLAPSED.
 *
 * 58 rows expanded is a wall; four rows collapsed is a pointless extra click.
 * The threshold picks the behaviour that suits the list actually in front of
 * the operator, and «توسيع الكل» is one button away either direction.
 */
const AUTO_COLLAPSE_ABOVE = 20;

/**
 * The delete warning, as paragraphs.
 *
 * The text is carried over verbatim from the `window.confirm` this replaced —
 * every clause in it was there for a measured reason:
 *
 *   - the COUNT is `countPlacements`, not `children.length`: the main menu
 *     nests three deep, so promising «2 عنصر فرعي» and removing nine is the
 *     failure this line exists to prevent. It is marked `strong` so it reads
 *     as the number being decided on, not as prose.
 *   - the SCOPE sentence stays because this removes the PLACEMENTS — the
 *     branch's presence in THIS menu — while the items themselves survive and
 *     can be added back. Without it «سيتم الحذف» reads as destroying the
 *     content itself.
 *
 * Returned as an array so the dialog can keep them apart; `window.confirm`
 * flattened them into one block.
 */
const buildDeleteWarning = (placement, active) => {
  const title = placement.item?.title ?? '';
  const descendants = countPlacements(placement.children);

  if (descendants > 0) {
    return [
      `سيتم حذف «${title}» وكل العناصر الفرعية التابعة له من «${active.label}».`,
      {
        strong: true,
        text: `عدد العناصر التي ستُحذف: ${descendants + 1} — العنصر الرئيسي و${descendants} عنصر فرعي.`,
      },
      'الحذف يتم من هذه القائمة فقط — العناصر نفسها تبقى محفوظة ويمكن إضافتها لاحقاً، ولا تتأثر القوائم الأخرى التي تظهر فيها.',
    ];
  }

  return [
    `سيتم حذف «${title}» من «${active.label}».`,
    'الحذف يتم من هذه القائمة فقط — العنصر نفسه يبقى محفوظاً ويمكن إضافته لاحقاً.',
  ];
};

const MenuSettings = ({ title, hint }) => {
  const [activeType, setActiveType] = useState(PLACEMENTS[0].type);
  const [editor, setEditor] = useState(null); // { placement } | { parentPlacementId }
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState('');
  const [collapsedIds, setCollapsedIds] = useState([]);
  // Drag-to-reparent, owned HERE and not in the tree: the drop is planned
  // against the UNFILTERED tree, which only this component has.
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // { id, position }
  const tabRefs = useRef([]);

  const {
    mode,
    updatedOn,
    isLoading: isModeLoading,
    isEndpointMissing,
  } = useMenuMode();
  const setMenuMode = useSetMenuMode();

  const {
    data: placementPage,
    isLoading: isPlacementsLoading,
    isError: isPlacementsError,
    error: placementsError,
  } = usePlacements(activeType);

  // All five menus at once, for the count badge on each tab. `null` item id —
  // only the per-type row counts are read here. Shares the placement query
  // keys, so the active tab's list is never fetched twice.
  const { byType: placementMap } = useItemPlacements(null);

  const updatePlacement = useUpdatePlacement();
  const deletePlacement = useDeletePlacement();
  const reorderSiblings = useReorderSiblings();
  const movePlacement = useMovePlacement();

  const tree = useMemo(
    () => buildPlacementTree(placementPage?.items),
    [placementPage]
  );

  const active = PLACEMENTS.find((p) => p.type === activeType) ?? PLACEMENTS[0];
  const activeIndex = PLACEMENTS.findIndex((p) => p.type === activeType);
  const isControlled = mode === MENU_MODE.CONTROLLED;
  const total = countPlacements(tree);
  const atCap = Boolean(active.maxItems) && tree.length >= active.maxItems;

  const isFiltering = filter.trim().length > 0;
  const visibleTree = useMemo(
    () => filterPlacementTree(tree, filter),
    [tree, filter]
  );
  const visibleCount = countPlacements(visibleTree);

  /**
   * Opening a long list collapsed, a short one expanded. Keyed on the tab so
   * switching menus re-decides; an operator's own expand/collapse inside a tab
   * survives until they leave it.
   */
  useEffect(() => {
    setFilter('');
    setDraggingId(null);
    setDropTarget(null);
    setCollapsedIds(
      countPlacements(tree) > AUTO_COLLAPSE_ABOVE ? collectParentIds(tree) : []
    );
    // `tree` is intentionally NOT a dependency: it changes identity on every
    // refetch, and re-collapsing the tree under someone mid-edit is worse than
    // a slightly stale default.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  // While filtering, every surviving branch is shown open — a match hidden
  // inside a collapsed parent is a search that looks broken.
  const effectiveCollapsed = isFiltering ? [] : collapsedIds;
  const parentIds = useMemo(() => collectParentIds(tree), [tree]);
  const allCollapsed =
    parentIds.length > 0 && collapsedIds.length >= parentIds.length;

  const handleToggleCollapse = (id) =>
    setCollapsedIds((previous) =>
      previous.includes(id)
        ? previous.filter((value) => value !== id)
        : [...previous, id]
    );

  /* ------------------------------------------------------------------ mode */

  const handleModeChange = (nextMode) => {
    if (nextMode === mode || setMenuMode.isLoading) return;

    if (nextMode === MENU_MODE.CONTROLLED) {
      const confirmed = window.confirm(
        'سيتم عرض القوائم في الموقع حسب الإعدادات المحفوظة هنا.\n\n' +
          'أي قائمة فارغة لن يظهر منها أي عنصر في الموقع — القائمة الافتراضية ' +
          'لم تعد تظهر بديلاً عنها، فتأكّد من تعبئة كل قائمة قبل التفعيل.' +
          '\n\nولن تفقد أي بيانات عند الرجوع إلى الوضع الحالي.\n\nهل تريد المتابعة؟'
      );
      if (!confirmed) return;
    }

    setMenuMode.mutate(nextMode, {
      onSuccess: () =>
        toast.success(
          nextMode === MENU_MODE.CONTROLLED
            ? 'تم تفعيل التحكم بالقوائم.'
            : 'تمت العودة إلى الوضع الحالي. الإعدادات محفوظة كما هي.'
        ),
      onError: (error) =>
        toast.error(error?.message || 'تعذّر تحديث طريقة العرض.'),
    });
  };

  /* ------------------------------------------------------------------ tabs */

  /**
   * Arrow-key navigation, per the WAI-ARIA tabs pattern.
   *
   * RTL-AWARE: the tabs run right-to-left on screen, so ArrowLeft moves to the
   * NEXT tab and ArrowRight to the previous. Wiring these the LTR way puts the
   * focus ring somewhere other than where the key pointed, which is worse than
   * having no arrow support at all.
   */
  const handleTabKeyDown = (event) => {
    const last = PLACEMENTS.length - 1;
    let next = null;

    if (event.key === 'ArrowLeft') next = activeIndex === last ? 0 : activeIndex + 1;
    else if (event.key === 'ArrowRight') next = activeIndex === 0 ? last : activeIndex - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;

    if (next === null) return;
    event.preventDefault();
    setActiveType(PLACEMENTS[next].type);
    tabRefs.current[next]?.focus();
  };

  /* -------------------------------------------------------------- row acts */

  /**
   * Every row action carries the placement's CURRENT values alongside the one
   * field being changed. `PUT /Placements/{id}` is a full replace, so sending
   * only the changed field would null out `linkOverride` and re-parent the row.
   */
  const handleToggleVisible = async (placement) => {
    setBusyId(placement.id);
    try {
      await updatePlacement.mutateAsync({
        id: placement.id,
        navigationItemId: placement.navigationItemId,
        placementType: placement.placementType,
        parentPlacementId: placement.parentPlacementId ?? null,
        linkOverride: placement.linkOverride ?? null,
        isVisible: placement.isVisible === false,
        sequenceId: placement.sequenceId ?? 0,
      });
    } catch (error) {
      toast.error(error?.message || 'تعذّر تغيير حالة الظهور.');
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Deleting a parent takes its whole branch with it.
   *
   * The count is `countPlacements`, NOT `children.length`: the main menu
   * nests three levels, so a row with 2 children and 7 grandchildren would
   * have promised «2 عنصر فرعي» and removed nine. The warning has to name
   * what the click actually does, at every depth.
   *
   * The scope sentence matters just as much. This removes the PLACEMENTS —
   * the branch's presence in THIS menu — while the items themselves survive
   * and can be added back, including wherever else they are placed. Saying
   * only «سيتم الحذف» would read as destroying the content itself.
   */
  // The row awaiting confirmation, or null. Holding the PLACEMENT rather than
  // a boolean keeps the dialog's text derived from one source — the row it is
  // actually about — so the count can never describe a different row.
  const [pendingDelete, setPendingDelete] = useState(null);

  const handleDelete = (placement) => setPendingDelete(placement);

  const confirmDelete = async () => {
    const placement = pendingDelete;
    if (!placement) return;

    const title = placement.item?.title ?? '';
    const descendants = countPlacements(placement.children);

    setBusyId(placement.id);
    try {
      await deletePlacement.mutateAsync(placement.id);
      toast.success(
        descendants > 0
          ? `تم حذف «${title}» و${descendants} عنصر فرعي من القائمة.`
          : 'تم حذف العنصر من القائمة.'
      );
      setPendingDelete(null);
    } catch (error) {
      toast.error(error?.message || 'تعذّر حذف العنصر.');
      // The dialog STAYS OPEN on failure: closing it would leave the operator
      // with a toast and no way to retry the thing they just asked for.
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Move one row within its own sibling group.
   *
   * Resolves the group from the UNFILTERED tree rather than trusting the
   * caller: while a filter is active the rows on screen are not the real
   * sibling group, and reordering against the filtered view would write
   * sequence numbers that scramble the rows the filter hid.
   */
  const handleMove = async (placement, direction) => {
    const findSiblings = (nodes) => {
      if (nodes.some((node) => node.id === placement.id)) return nodes;
      for (const node of nodes) {
        const found = findSiblings(node.children ?? []);
        if (found) return found;
      }
      return null;
    };

    const siblings = findSiblings(tree);
    if (!siblings) return;

    const from = siblings.findIndex((node) => node.id === placement.id);
    const to = from + direction;
    if (to < 0 || to >= siblings.length) return;

    const reordered = [...siblings];
    [reordered[from], reordered[to]] = [reordered[to], reordered[from]];

    setBusyId(placement.id);
    try {
      await reorderSiblings.mutateAsync({ placements: reordered });
    } catch (error) {
      toast.error(error?.message || 'تعذّر تغيير الترتيب.');
    } finally {
      setBusyId(null);
    }
  };

  /* ------------------------------------------------------------- drag move */

  /**
   * Drag-to-reparent is offered on the NESTING menus only — «القائمة الرئيسية»
   * and «قائمة البحث». `supportsNesting` is the honest predicate rather than a
   * second flag listing the same two menus: a flat menu has no parent to move
   * a row between, so there is nothing the gesture could mean there.
   *
   * It is also off WHILE FILTERING, for the reason `handleMove` resolves its
   * sibling group from the unfiltered tree: the rows on screen are not the real
   * group, so a drop planned against them would write sequence numbers that
   * scramble the rows the filter hid.
   */
  const canDragMove = Boolean(active.supportsNesting) && !isFiltering;

  const planFor = (target, position) =>
    planPlacementMove(tree, {
      movedId: draggingId,
      targetId: target?.id,
      position,
    });

  // Asked on every `dragover`, so it must stay cheap and must not touch state.
  // Answering from the same planner that performs the move is what keeps the
  // indicator and the write from ever disagreeing.
  const canDropOn = (target, position) =>
    canDragMove && Boolean(planFor(target, position));

  const handleRowDragStart = (placement) => {
    setDraggingId(placement.id);
    setDropTarget(null);
  };

  const handleRowDragOver = (placement, position) =>
    setDropTarget((previous) =>
      previous?.id === placement.id && previous?.position === position
        ? previous // same slot — skip the re-render `dragover` would cause 60x/s
        : { id: placement.id, position }
    );

  const handleRowDragLeave = (placement) =>
    setDropTarget((previous) =>
      previous?.id === placement.id ? null : previous
    );

  const handleRowDragEnd = () => {
    setDraggingId(null);
    setDropTarget(null);
  };

  /**
   * Land the drag.
   *
   * The plan is recomputed from the drop event rather than read from
   * `dropTarget`: the state is one `dragover` behind by definition, and
   * committing the previous frame's target would move the row somewhere the
   * operator was no longer pointing.
   */
  const handleRowDrop = async (target, position) => {
    const plan = planFor(target, position);
    const movedId = draggingId;
    setDraggingId(null);
    setDropTarget(null);
    if (!plan || !movedId) return;

    const title = plan.moved.item?.title ?? '';
    const parentTitle =
      plan.parentPlacementId === null
        ? null
        : (findPlacementNode(tree, plan.parentPlacementId)?.item?.title ?? '');

    setBusyId(movedId);
    try {
      await movePlacement.mutateAsync(plan);
      // Show where it went: dropping into a collapsed parent would otherwise
      // read as the row having disappeared.
      if (plan.parentPlacementId) {
        setCollapsedIds((previous) =>
          previous.filter((id) => id !== plan.parentPlacementId)
        );
      }
      toast.success(
        parentTitle
          ? `تم نقل «${title}» تحت «${parentTitle}».`
          : `تم نقل «${title}» إلى المستوى الأول.`
      );
    } catch (error) {
      toast.error(error?.message || 'تعذّر نقل العنصر.');
    } finally {
      setBusyId(null);
    }
  };

  /* ------------------------------------------------------------------ view */

  const panelId = `menu-panel-${activeType}`;

  return (
    <div className={styles['menu-settings']}>
      {/* ---------------------------------------------------------------- */}
      {/* Page header — title, and the one question this screen answers     */}
      {/* ---------------------------------------------------------------- */}
      <header className={styles['page-head']}>
        <div>
          <h1 className={styles['page-title']}>{title ?? 'إعدادات القوائم'}</h1>
          <p className={styles['page-subtitle']}>
            {hint ?? 'تحدد مصدر القوائم التي تظهر في الموقع، وترتيب عناصر كل قائمة.'}
          </p>
        </div>

        {!isModeLoading && (
          <span
            className={`${styles.status} ${
              isControlled ? styles['status-live'] : styles['status-idle']
            }`}
          >
            <span className={styles['status-dot']} aria-hidden='true' />
            {isControlled
              ? 'هذه الإعدادات تظهر في الموقع'
              : 'هذه الإعدادات لا تظهر في الموقع'}
          </span>
        )}
      </header>

      {/* ---------------------------------------------------------------- */}
      {/* كيفية العرض                                                       */}
      {/* ---------------------------------------------------------------- */}
      <section className={`${styles.card} ${styles['mode-card']}`}>
        <header className={styles['mode-header']}>
          <h2 className={styles['mode-title']}>كيفية العرض</h2>
          <p className={styles['mode-subtitle']}>
            تحدد هذه الإعدادات مصدر القوائم التي تظهر في الموقع.
          </p>
        </header>

        {isModeLoading ? (
          <Loader />
        ) : (
          <div className={styles['mode-options']}>
            <label
              className={`${styles['mode-option']} ${
                !isControlled ? styles['mode-option-active'] : ''
              }`}
            >
              <input
                type='radio'
                name='menuMode'
                checked={!isControlled}
                disabled={isEndpointMissing || setMenuMode.isLoading}
                onChange={() => handleModeChange(MENU_MODE.LEGACY)}
              />
              <span>
                <strong>كما هو حالياً</strong>
                <em className={styles['mode-default']}>افتراضي</em>
                <small>
                  يعرض الموقع القوائم المبنية داخله، تماماً كما هي اليوم.
                </small>
              </span>
            </label>

            <label
              className={`${styles['mode-option']} ${
                isControlled ? styles['mode-option-active'] : ''
              }`}
            >
              <input
                type='radio'
                name='menuMode'
                checked={isControlled}
                disabled={isEndpointMissing || setMenuMode.isLoading}
                onChange={() => handleModeChange(MENU_MODE.CONTROLLED)}
              />
              <span>
                <strong>التحكم بالقوائم</strong>
                <small>
                  يعرض الموقع القوائم حسب ما تم إعداده في هذه الصفحة. يمكن
                  الرجوع إلى الوضع الحالي في أي وقت دون فقدان الإعدادات.
                </small>
              </span>
            </label>
          </div>
        )}

        {isEndpointMissing && (
          <p className={styles['notice-blocked']}>
            خدمة حفظ طريقة العرض غير متوفرة حالياً
            <code> GET/PUT /api/NavigationMenu/Settings</code>. يمكن إعداد
            القوائم الآن، وسيتم تفعيل هذا الخيار فور توفّر الخدمة.
          </p>
        )}

        {!isControlled && !isModeLoading && (
          <p className={styles['notice-inactive']}>
            كل ما تعدّله بالأسفل محفوظ، لكنه لا يؤثر على الموقع حتى تختار «التحكم
            بالقوائم».
          </p>
        )}

        {!isEndpointMissing && updatedOn && (
          <p className={styles['mode-meta']}>
            آخر تحديث: {new Date(updatedOn).toLocaleString('ar-AE')}
          </p>
        )}
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* Placement tabs + panel                                            */}
      {/* ---------------------------------------------------------------- */}
      <div>
        <div
          className={styles.tablist}
          role='tablist'
          aria-label='قوائم الموقع'
          onKeyDown={handleTabKeyDown}
        >
          {PLACEMENTS.map((placement, index) => {
            const info = placementMap.find(
              (entry) => entry.type === placement.type
            );
            // The badge appears only once its list has actually loaded — a «0»
            // that means «still fetching» would read as an empty menu.
            const count =
              info && !info.isLoading && !info.isError ? info.totalRows : null;
            const isActive = placement.type === activeType;
            // Flagged amber only when it MATTERS: an empty required menu makes
            // the website fall back to its built-in one, but only once the mode
            // is live, and `optional` menus are meant to be empty.
            const isEmptyRisk =
              count === 0 && isControlled && !placement.optional;

            return (
              <button
                key={placement.type}
                type='button'
                role='tab'
                id={`menu-tab-${placement.type}`}
                aria-selected={isActive}
                aria-controls={isActive ? panelId : undefined}
                tabIndex={isActive ? 0 : -1}
                ref={(node) => {
                  tabRefs.current[index] = node;
                }}
                className={[
                  styles.tab,
                  isActive ? styles['tab-active'] : '',
                  isEmptyRisk ? styles['tab-empty'] : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setActiveType(placement.type)}
                title={placement.hint}
              >
                {placement.label}
                {count !== null && (
                  <span className={styles['tab-count']}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        <section
          className={`${styles.card} ${styles.panel}`}
          id={panelId}
          role='tabpanel'
          aria-labelledby={`menu-tab-${activeType}`}
          tabIndex={-1}
        >
          <header className={styles['panel-head']}>
            <div className={styles['panel-heading']}>
              <h2 className={styles['panel-title']}>{active.label}</h2>
              <p className={styles['panel-hint']}>{active.hint}</p>
            </div>

            <div className={styles['panel-tools']}>
              <input
                type='search'
                className={styles.filter}
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder='بحث في هذه القائمة…'
                aria-label={`بحث في ${active.label}`}
                disabled={total === 0}
              />

              {parentIds.length > 0 && (
                <button
                  type='button'
                  className={styles['tool-button']}
                  onClick={() => setCollapsedIds(allCollapsed ? [] : parentIds)}
                  disabled={isFiltering}
                  title={
                    isFiltering
                      ? 'البحث يعرض كل النتائج موسّعة'
                      : undefined
                  }
                >
                  {allCollapsed ? ICONS.expandAll : ICONS.collapseAll}
                  {allCollapsed ? 'توسيع الكل' : 'طيّ الكل'}
                </button>
              )}

              <button
                type='button'
                className={`${styles['tool-button']} ${styles['tool-primary']}`}
                onClick={() => setEditor({ parentPlacementId: null })}
                disabled={atCap}
                title={
                  atCap
                    ? `هذه القائمة تقبل ${active.maxItems} عناصر كحد أقصى`
                    : undefined
                }
              >
                {ICONS.add}
                إضافة عنصر
              </button>
            </div>
          </header>

          <div className={styles['panel-body']}>
            {atCap && (
              <p className={styles['notice-info']}>
                هذه القائمة تقبل {active.maxItems} عناصر كحد أقصى. الشريط العلوي
                مصمم على هذا العدد في أصغر مقاس شاشة مدعوم؛ تجاوزه يحتاج مراجعة
                تصميمية أولاً.
              </p>
            )}

            {isPlacementsLoading ? (
              <Loader />
            ) : isPlacementsError ? (
              <p className={styles['notice-blocked']}>
                {placementsError?.message || 'تعذّر تحميل عناصر القائمة.'}
              </p>
            ) : (
              <>
                {/* The website changed under this message on 2026-09-29: an
                    empty menu under «التحكم بالقوائم» now renders NOTHING
                    rather than falling back to the built-in menu. Saying
                    otherwise would send an operator looking for a fallback
                    that no longer exists. */}
                {isControlled && total === 0 && !active.optional && (
                  <p className={styles['notice-warning']}>
                    هذه القائمة فارغة، ولن يظهر منها أي عنصر في الموقع. القائمة
                    الافتراضية لم تعد تظهر بديلاً عنها ما دام «التحكم بالقوائم»
                    مفعّلاً.
                  </p>
                )}
                {active.optional && total === 0 && (
                  <p className={styles['notice-info']}>
                    هذه القائمة اختيارية. عند تركها فارغة يعرض الموقع القائمة
                    الرئيسية في نافذة البحث.
                  </p>
                )}

                {isFiltering && (
                  <p className={styles['filter-summary']}>
                    <strong>{visibleCount}</strong> من {total} عنصر يطابق «
                    {filter.trim()}».
                  </p>
                )}

                {/* Say the gesture exists. A grip nobody notices is a feature
                    nobody has; and when a filter turns it off, say THAT too,
                    rather than leaving a grip that silently stopped working. */}
                {active.supportsNesting && total > 0 && (
                  <p className={styles['notice-info']}>
                    {isFiltering
                      ? 'نقل العناصر بالسحب متوقّف أثناء البحث — امسح البحث لتتمكن من إعادة ترتيب العناصر ونقلها.'
                      : 'يمكن سحب العنصر من المقبض لنقله: أفلته فوق عنصر ليصبح تابعاً له، أو بين عنصرين لتغيير ترتيبه.'}
                  </p>
                )}

                <MenuTree
                  nodes={visibleTree}
                  placement={active}
                  busyId={busyId}
                  collapsedIds={effectiveCollapsed}
                  onToggleCollapse={handleToggleCollapse}
                  emptyMessage={
                    isFiltering
                      ? `لا يوجد عنصر يطابق «${filter.trim()}» في هذه القائمة.`
                      : 'لا توجد عناصر في هذه القائمة بعد.'
                  }
                  onAddFirst={
                    atCap || isFiltering
                      ? null
                      : () => setEditor({ parentPlacementId: null })
                  }
                  onEdit={(placement) => setEditor({ placement })}
                  onAddChild={(parent) =>
                    setEditor({ parentPlacementId: parent.id })
                  }
                  onToggleVisible={handleToggleVisible}
                  onDelete={handleDelete}
                  onMove={handleMove}
                  dragEnabled={canDragMove}
                  draggingId={draggingId}
                  dropTarget={dropTarget}
                  canDropOn={canDropOn}
                  onRowDragStart={handleRowDragStart}
                  onRowDragOver={handleRowDragOver}
                  onRowDragLeave={handleRowDragLeave}
                  onRowDrop={handleRowDrop}
                  onRowDragEnd={handleRowDragEnd}
                />
              </>
            )}
          </div>
        </section>
      </div>

      {editor && (
        <MenuItemEditor
          placement={editor.placement ?? null}
          placementType={activeType}
          placementConfig={active}
          parentPlacementId={editor.parentPlacementId ?? null}
          nextSequenceId={tree.length}
          onClose={() => setEditor(null)}
        />
      )}

      {/* Was `window.confirm`. Same warning, same counts — rendered in the
          screen's own language and direction, with the paragraphs kept apart
          and the destructive action visually distinct from the safe one.
          See ConfirmDialog.jsx. */}
      {pendingDelete && (
        <ConfirmDialog
          tone='danger'
          title={`حذف «${pendingDelete.item?.title ?? ''}» من «${active.label}»؟`}
          lines={buildDeleteWarning(pendingDelete, active)}
          confirmLabel={
            countPlacements(pendingDelete.children) > 0
              ? `حذف ${countPlacements(pendingDelete.children) + 1} عنصر`
              : 'حذف العنصر'
          }
          isBusy={busyId === pendingDelete.id}
          onConfirm={confirmDelete}
          onClose={() => {
            // A delete in flight must not be abandoned half-way.
            if (busyId === pendingDelete.id) return;
            setPendingDelete(null);
          }}
        />
      )}
    </div>
  );
};

export default MenuSettings;
